import { Stack, Group, Text, Badge, Alert, Card, Table, Paper, Loader } from '@mantine/core';
import { IconAlertCircle, IconRocket } from '@tabler/icons-react';
import {
  FACTORY_BUILD_STATUS_LABELS,
  FACTORY_BUILD_STATUS_COLORS,
  FACTORY_SUBMISSION_STATE_LABELS,
  FACTORY_SUBMISSION_STATE_COLORS,
} from '@/lib/constants/appFactory';
import type { AppBuildVersion } from '@/lib/types/App';
import { AppFactoryUtils } from '../AppFactoryUtils';
import {
  useFactoryBuilds,
  useFactoryReleases,
  useFactorySubmissions,
  useFactoryTasks,
} from '../useAppFactory';
import { formatTimestamp } from '@/shared/utils/date';

interface AppRokuStatusTabProps {
  appCfgId: number;
  legacyBuildVersions: AppBuildVersion[];
}

/**
 * The customer-facing App Center status surface: current live version,
 * action-needed tasks, submission status, build history, and the release
 * changelog feed. Read-only by design — updates ship through operator-run
 * campaigns, never a customer upgrade button.
 */
export function AppRokuStatusTab({ appCfgId, legacyBuildVersions }: AppRokuStatusTabProps) {
  const buildsQuery = useFactoryBuilds(appCfgId);
  const submissionsQuery = useFactorySubmissions(appCfgId);
  const releasesQuery = useFactoryReleases();
  const tasksQuery = useFactoryTasks();

  const isLoading =
    buildsQuery.isLoading ||
    submissionsQuery.isLoading ||
    releasesQuery.isLoading ||
    tasksQuery.isLoading;
  const unavailable = buildsQuery.isError || submissionsQuery.isError;

  if (isLoading) {
    return (
      <Group justify="center" p="xl">
        <Loader size="sm" />
        <Text c="dimmed" size="sm">
          Loading build status...
        </Text>
      </Group>
    );
  }

  if (unavailable) {
    return (
      <Stack gap="lg">
        <Alert icon={<IconAlertCircle size={16} />} color="gray">
          Build and submission status is currently unavailable. Your app settings are not
          affected.
        </Alert>
        <LegacyBuilds legacyBuildVersions={legacyBuildVersions} />
      </Stack>
    );
  }

  const builds = AppFactoryUtils.sortByCreatedDesc(buildsQuery.data?.builds ?? []);
  const submissions = AppFactoryUtils.sortByCreatedDesc(
    submissionsQuery.data?.submissions ?? [],
  );
  const releases = AppFactoryUtils.sortByCreatedDesc(releasesQuery.data?.releases ?? []);
  const appTasks = AppFactoryUtils.tasksForApp(tasksQuery.data?.tasks ?? [], appCfgId);
  const liveInfo = AppFactoryUtils.currentLiveInfo(submissions, builds, releases);
  const releaseById = new Map(releases.map((r) => [r.shellReleaseId, r]));
  // Farm liveness chip: absent unless the status payload carries the farm
  // field (daemons since the AppFactory split).
  const farm = AppFactoryUtils.farmLiveness(tasksQuery.data?.farm, Date.now());

  return (
    <Stack gap="lg">
      {/* Current live version */}
      <Card withBorder radius="md" p="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="md">
            <IconRocket size={28} color="var(--mantine-color-green-6)" />
            {liveInfo ? (
            <div>
              <Group gap="xs">
                <Text fw={600}>Version {liveInfo.release?.semver ?? '—'}</Text>
                <Badge variant="light" color="green" size="sm">
                  Live
                </Badge>
                {liveInfo.release && (
                  <Badge variant="outline" color="gray" size="sm">
                    Lane {liveInfo.release.lane}
                  </Badge>
                )}
              </Group>
              <Text size="sm" c="dimmed">
                Live in the store since {AppFactoryUtils.formatWhen(liveInfo.submission.decidedAt)}
              </Text>
            </div>
          ) : (
            <div>
              <Text fw={600}>Not live yet</Text>
              <Text size="sm" c="dimmed">
                This app has not gone live through App Center. Status appears here as builds
                and submissions progress.
              </Text>
            </div>
            )}
          </Group>
          {farm && (
            <Badge variant="light" color={farm.stale ? 'orange' : 'green'} size="sm">
              {farm.label}
            </Badge>
          )}
        </Group>
      </Card>

      {/* Action needed */}
      {appTasks.length > 0 && (
        <Stack gap="xs">
          <Text fw={600} size="md">
            Action Needed
          </Text>
          {appTasks.map((t) => (
            <Alert
              key={`${t.kind}-${t.id}`}
              icon={<IconAlertCircle size={16} />}
              color="orange"
            >
              {t.description}
            </Alert>
          ))}
        </Stack>
      )}

      {/* Submissions */}
      {submissions.length > 0 && (
        <Stack gap="xs">
          <Text fw={600} size="md">
            Store Submissions
          </Text>
          <Paper withBorder style={{ overflow: 'clip' }}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Store</Table.Th>
                  <Table.Th>Submitted</Table.Th>
                  <Table.Th>Decided</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {submissions.map((s) => (
                  <Table.Tr key={s.appSubmissionId}>
                    <Table.Td>
                      <Badge
                        variant="light"
                        color={FACTORY_SUBMISSION_STATE_COLORS[s.state] ?? 'gray'}
                        size="sm"
                      >
                        {FACTORY_SUBMISSION_STATE_LABELS[s.state] ?? s.state}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{s.store}</Table.Td>
                    <Table.Td>{AppFactoryUtils.formatWhen(s.submittedAt)}</Table.Td>
                    <Table.Td>{AppFactoryUtils.formatWhen(s.decidedAt)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        </Stack>
      )}

      {/* Build history */}
      {builds.length > 0 && (
        <Stack gap="xs">
          <Text fw={600} size="md">
            Build History
          </Text>
          <Paper withBorder style={{ overflow: 'clip' }}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Version</Table.Th>
                  <Table.Th>Platform</Table.Th>
                  <Table.Th>Started</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {builds.map((b) => (
                  <Table.Tr key={b.appBuild2Id}>
                    <Table.Td>
                      <Badge
                        variant="light"
                        color={FACTORY_BUILD_STATUS_COLORS[b.status] ?? 'gray'}
                        size="sm"
                      >
                        {FACTORY_BUILD_STATUS_LABELS[b.status] ?? b.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{releaseById.get(b.shellReleaseId)?.semver ?? '—'}</Table.Td>
                    <Table.Td>{b.platform}</Table.Td>
                    <Table.Td>{AppFactoryUtils.formatWhen(b.created)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        </Stack>
      )}

      {/* Release notes feed — no upgrade button by design */}
      {releases.length > 0 && (
        <Stack gap="xs">
          <Text fw={600} size="md">
            Release Notes
          </Text>
          {releases.map((r) => (
            <Card key={r.shellReleaseId} withBorder radius="md" p="md">
              <Group gap="xs" mb={4}>
                <Text fw={600} size="sm">
                  {r.semver}
                </Text>
                <Badge variant="outline" color="gray" size="xs">
                  Lane {r.lane}
                </Badge>
                <Text size="xs" c="dimmed">
                  {AppFactoryUtils.formatWhen(r.created)}
                </Text>
              </Group>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                {r.changelog}
              </Text>
            </Card>
          ))}
        </Stack>
      )}

      <LegacyBuilds legacyBuildVersions={legacyBuildVersions} />
    </Stack>
  );
}

// ── Internal: legacy (V1 pipeline) build history ──

function LegacyBuilds({ legacyBuildVersions }: { legacyBuildVersions: AppBuildVersion[] }) {
  if (legacyBuildVersions.length === 0) return null;
  const rows = [...legacyBuildVersions].sort((a, b) => b.build.created - a.build.created);
  return (
    <Stack gap="xs">
      <Text fw={600} size="md">
        Earlier Builds
      </Text>
      <Text size="xs" c="dimmed">
        Builds from before App Center.
      </Text>
      <Paper withBorder style={{ overflow: 'clip' }}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Version</Table.Th>
              <Table.Th>Created</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((bv) => (
              <Table.Tr key={bv.build.appBuildId}>
                <Table.Td>{bv.version.version}</Table.Td>
                <Table.Td>{formatTimestamp(bv.build.created)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>
    </Stack>
  );
}
